import React, { useCallback, useMemo } from "react";

import { faMobile, faSync } from "@fortawesome/free-solid-svg-icons";
import { Grid } from "@material-ui/core";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components/macro";
import { v4 } from "uuid";

import {
  closeConfirmModal,
  openConfirmModal,
  openDispatchingModal,
  RuleBookAction,
} from "actions";

import { ColDef } from "ag-grid-community";

import AgGridTable from "components/AgGrid/AgGridContainer";
import TableWrapper from "components/AgGrid/TableWrapper";
import { Column, Row } from "components/Layout";
import WaitForSync from "components/LoadingSpinner/WaitForSync";
import { ruleColumnDefs } from "components/RuleBookTable/columns";

import { AgGridTables } from "constants/aggrid";
import { ApiModel } from "constants/loading";
import { ModalTypes } from "constants/navigation";
import { RuleBookPermissions } from "constants/permissions";
import { ValueSource } from "constants/ruleBooks";

import { getGstFee } from "containers/Settings/RuleBooks/Rules/CustomRules/Value/ValueClause";
import { EMPTY_SET } from "lib";
import { openModalLink } from "lib/navigation";
import { hasPermission } from "lib/permissions";

import { getMasterRuleBookIdsByRuleBookId, getRuleBookById } from "selectors";

import { selectRulesAggridDataByRuleBookIdLookup } from "selectors/aggrid/rules";

import { useHasCreateRulePermission } from "hooks/useHasCreateRuleBookPermission";

import { RuleBookSummary } from "./RuleBookSummary";
import { RuleBookSummaryForm } from "./RuleBookSummaryForm";

const RuleBookSummaryGrid = styled(Grid)`
  background: ${({ theme }) => theme.colors.grayF3};
  border-radius: 4px;
  padding: ${({ theme }) => theme.space[2]}px;
  margin-bottom: ${({ theme }) => theme.space[2]}px;
`;

const getRowId = params => params.data.rule.id;

interface RuleBookSummaryComponentProps {
  ruleBookId: string;
  readOnly: boolean;
  openEditSettings: () => void;
  isEditSettingsOpen: boolean;
}

const RuleBookSummaryComponent = ({
  ruleBookId,
  openEditSettings,
  isEditSettingsOpen,
  readOnly,
}: RuleBookSummaryComponentProps) => (
  <RuleBookSummaryGrid container>
    {isEditSettingsOpen ? (
      <RuleBookSummaryForm />
    ) : (
      <RuleBookSummary
        ruleBookId={ruleBookId}
        readOnly={readOnly}
        openEditSettings={openEditSettings}
      />
    )}
  </RuleBookSummaryGrid>
);

interface RuleBookTableProps {
  ruleBookId: string;
  isEditSettingsOpen: boolean;
  openEditSettings: () => void;
}

export const RuleBookTable = ({
  ruleBookId,
  isEditSettingsOpen,
  openEditSettings,
}: RuleBookTableProps) => {
  const rowData = useSelector(selectRulesAggridDataByRuleBookIdLookup)[
    ruleBookId
  ];

  const ruleBook = useSelector(getRuleBookById(ruleBookId));

  const readOnly = !hasPermission(ruleBook, RuleBookPermissions.update);

  const hasCreateRulePermission = useHasCreateRulePermission();

  const dispatch = useDispatch();

  const masterRuleBookIds: Set<string> =
    useSelector(getMasterRuleBookIdsByRuleBookId(ruleBookId)) || EMPTY_SET;

  const onClickSynchronizAllManagedRules = useCallback(() => {
    masterRuleBookIds.forEach(masterRuleBookId => {
      dispatch(
        RuleBookAction.synchronizeWithMasterRuleBook({
          id: ruleBookId,
          masterRuleBookId,
        }),
      );
    });
    dispatch(closeConfirmModal());
  }, [dispatch, masterRuleBookIds, ruleBookId]);

  const onClickSynchronizAllManagedRulesConfirmationDialog = useCallback(() => {
    dispatch(
      openConfirmModal({
        title: "Are you sure?",
        message:
          "Are you sure you want to synchronise all master rulebooks used in this rule book?",
        actions: [
          {
            label: "Cancel",
            onClick: () => dispatch(closeConfirmModal()),
            secondary: true,
          },
          {
            label: "Synchronise",
            onClick: onClickSynchronizAllManagedRules,
            secondary: false,
          },
        ],
      }),
    );
  }, [dispatch, onClickSynchronizAllManagedRules]);

  const onClickAddCustomRule = useCallback(() => {
    dispatch(
      openDispatchingModal(ModalTypes.EditRule, window.location.hash, {
        rule: {
          criteria: [],
          category: "",
          comment: "",
          gl_code: { source: ValueSource.CONSTANT, value: "" },
          billing_tags: [],
          from_business: { source: ValueSource.FIELD, value: "" },
          id: v4(),
          include_if_zero: false,
          input_field: null,
          name: "",
          prerequisite_rule_ids: [],
          price_multiplier: 1,
          rule_book_id: ruleBookId,
          rule_set_id: null,
          to_business: { source: ValueSource.FIELD, value: "" },
          value: {
            amount: { source: ValueSource.CONSTANT, value: 0 },
            offset: { source: ValueSource.CONSTANT, value: 0 },
            rounding_value: 0,
            unit_offset: { source: ValueSource.CONSTANT, value: 0 },
            unit_limit: { source: ValueSource.CONSTANT, value: 0 },
            units: { source: ValueSource.CONSTANT, value: 0 },
            fees: [getGstFee("exclusive")],
          },
        },
      }),
    );
  }, [dispatch, ruleBookId]);

  const addManagedRule = useCallback(
    () =>
      dispatch(
        openDispatchingModal(
          ModalTypes.RuleLibrary,
          window.location.hash,
          { ruleBookId },
          { ruleBookId },
        ),
      ),
    [dispatch, ruleBookId],
  );

  const handleOpenRuleTester = useCallback(() => {
    // Stash the rule data in state - it's (probably) too big for the URL method.

    openModalLink(
      ModalTypes.RuleTester,
      [[], ruleBookId, false],
      window.location.hash,
    );
  }, [ruleBookId]);

  const additionalActions = useMemo(
    () =>
      [
        {
          title: "Add Managed Rule",
          icon: faMobile,
          onClick: addManagedRule,
          dataTour: "addManagedRule",
          isDisabled: !hasCreateRulePermission,
        },
        {
          title: "Add Custom Rule",
          icon: faMobile,
          onClick: onClickAddCustomRule,
          dataTour: "addCustomRule",
        },
        {
          title: "Test Rules",
          icon: faMobile,
          onClick: handleOpenRuleTester,
          dataTour: "testRules",
        },
        masterRuleBookIds && {
          title: "Synchronise All Managed Rules",
          icon: faSync,
          onClick: onClickSynchronizAllManagedRulesConfirmationDialog,
          dataTour: "synchronizAllManagedRules",
          isDisabled: !hasCreateRulePermission,
        },
      ].filter(Boolean),
    [
      addManagedRule,
      hasCreateRulePermission,
      onClickAddCustomRule,
      handleOpenRuleTester,
      masterRuleBookIds,
      onClickSynchronizAllManagedRulesConfirmationDialog,
    ],
  );

  // TODO: remove cast once ruleColumnDefs is typed to return ColDef[]
  const columnDefs = useMemo(() => ruleColumnDefs() as ColDef[], []);

  return (
    <Column fullHeight>
      <Row>
        <RuleBookSummaryComponent
          ruleBookId={ruleBookId}
          openEditSettings={openEditSettings}
          isEditSettingsOpen={isEditSettingsOpen}
          readOnly={readOnly}
        />
      </Row>
      <WaitForSync
        requiredData={[ApiModel.RULE_BOOKS, ApiModel.RULE_SETS, ApiModel.RULES]}
      >
        <AgGridTable
          additionalActions={additionalActions}
          columnDefs={columnDefs}
          getRowId={getRowId}
          rowData={rowData}
          showGlobalSearchFilter={false}
          WrapperComponent={TableWrapper}
          tableName={AgGridTables.RULE_BOOK}
          enableBrowserTooltips
        />
      </WaitForSync>
    </Column>
  );
};
